import type {
  CoachEngagement,
  EngagementListItem,
  Match,
  MatchSummary,
  Player,
  PlayerGoal,
} from "@/lib/types";

export type CoachFeedbackBoardLaneId =
  | "needs_touch"
  | "follow_up"
  | "shared"
  | "drafting";

export type CoachFeedbackBoardCardTone =
  | "default"
  | "accent"
  | "good"
  | "flag";

export type CoachFeedbackBoardAction =
  | {
      kind: "roster";
      playerId?: string;
      context?:
        | "need_help"
        | "tried_this"
        | "no_recent_feedback"
        | "unreviewed_feedback"
      | "stale_goal";
    }
  | { kind: "notes"; itemId?: number }
  | { kind: "clips"; itemId?: number }
  | { kind: "recaps"; itemId?: number; matchId?: string }
  | { kind: "summaries"; matchId?: string };

/** One `(responder, item)` pair behind a follow-up card. A card groups
 * one player's responses, and a player maps to several accounts
 * (themselves plus guardians), so the ack has to name the rows it means
 * — acking by item alone would clear every responder on a team note. */
export type CoachFeedbackAckTarget = {
  user_id: string;
  item_kind: "note" | "clip";
  item_id: number;
};

export type CoachFeedbackBoardCard = {
  id: string;
  lane: CoachFeedbackBoardLaneId;
  title: string;
  meta: string;
  description: string;
  tone: CoachFeedbackBoardCardTone;
  playerId?: string;
  action: CoachFeedbackBoardAction;
  /** Present only on follow-up cards derived from player responses. */
  ackTargets?: CoachFeedbackAckTarget[];
};

export type CoachFeedbackBoardLane = {
  id: CoachFeedbackBoardLaneId;
  title: string;
  description: string;
  cards: CoachFeedbackBoardCard[];
};

export type CoachFeedbackBoardSummary = {
  needsTouch: number;
  followUp: number;
  unreviewed: number;
  missingRecaps: number;
};

export type CoachFeedbackBoardModel = {
  lanes: CoachFeedbackBoardLane[];
  summary: CoachFeedbackBoardSummary;
};

export type BuildCoachFeedbackBoardInput = {
  players: Player[];
  engagement?: CoachEngagement;
  matches: Match[];
  summaries: MatchSummary[];
  canonicalRecaps?: { complete: boolean; matchIds: readonly string[] };
  /** The canonical Feedback Library cannot open a legacy note, clip or
   * recap by its numeric id (it shows an "older Library item" notice), so
   * cards about legacy items link to the player's row instead — the same
   * destination the bell uses for them. */
  canonicalFeedback?: boolean;
  goals?: PlayerGoal[];
};

const MAX_CARDS_PER_LANE = 6;

export function buildCoachFeedbackBoard({
  players,
  engagement,
  matches,
  summaries,
  canonicalRecaps,
  canonicalFeedback = false,
  goals = [],
}: BuildCoachFeedbackBoardInput): CoachFeedbackBoardModel {
  const activePlayers = players.filter((p) => p.active);
  const summaryMatchIds = new Set(canonicalRecaps ? canonicalRecaps.matchIds : summaries.map((summary) => summary.match_id));
  const activePlayersById = new Map(activePlayers.map((p) => [p.id, p]));

  const needsTouchCards = (engagement?.players_with_no_recent_feedback ?? [])
    .slice(0, MAX_CARDS_PER_LANE)
    .map((row): CoachFeedbackBoardCard => {
      const label = playerLabel({
        display_name: row.display_name,
        jersey_number: row.jersey_number,
      });
      return {
        id: `no-recent-${row.player_id}`,
        lane: "needs_touch",
        title: label,
        meta: "No recent feedback",
        description:
          "This active player has not received a recent player-facing touch.",
        tone: "flag",
        playerId: row.player_id,
        action: {
          kind: "roster",
          playerId: row.player_id,
          context: "no_recent_feedback",
        },
      };
    });

  const followUpCards = buildFollowUpCards(engagement, canonicalFeedback).slice(
    0,
    MAX_CARDS_PER_LANE,
  );

  const unreviewedAssignedItems = engagement?.unreviewed_assigned_items ?? [];
  const sharedCards = unreviewedAssignedItems
    .filter((item) => item.kind !== "playlist")
    .slice(0, MAX_CARDS_PER_LANE)
    .map((item): CoachFeedbackBoardCard => {
      const playerCount = item.player_ids?.length ?? 0;
      return {
        id: `unreviewed-${item.kind}-${item.item_id}`,
        lane: "shared",
        title: item.title?.trim() || fallbackItemTitle(item),
        meta: [
          item.kind,
          playerCount ? `${playerCount} player${playerCount === 1 ? "" : "s"}` : "",
          item.date ?? "",
        ]
          .filter(Boolean)
          .join(" · "),
        description: notOpenedSentence(item.player_ids, activePlayersById),
        tone: "accent",
        playerId: item.player_ids?.[0],
        action: sharedFallbackAction(item, canonicalFeedback),
      };
    });

  const staleGoalCards = goals
    .filter((goal) => isActiveGoal(goal) && activePlayersById.has(goal.player_id))
    .filter((goal) => goal.needs_coach_follow_up || isStaleGoal(goal))
    .sort((a, b) => String(a.target_date).localeCompare(String(b.target_date)))
    .slice(0, MAX_CARDS_PER_LANE)
    .map((goal): CoachFeedbackBoardCard => {
      const player = activePlayersById.get(goal.player_id);
      return {
        id: `goal-${goal.id}`,
        lane: "follow_up",
        title: player ? playerLabel(player) : goal.title,
        meta: goal.needs_coach_follow_up
          ? "Goal needs follow-up"
          : "Goal stale",
        description: goal.title,
        tone: "flag",
        playerId: goal.player_id,
        action: {
          kind: "roster",
          playerId: goal.player_id,
          context: "stale_goal",
        },
      };
    });

  const missingRecapCards = (canonicalRecaps && !canonicalRecaps.complete ? [] : matches)
    .filter((match) => match.date && !summaryMatchIds.has(match.id))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, MAX_CARDS_PER_LANE)
    .map((match): CoachFeedbackBoardCard => {
      const label = `${match.home_team} vs ${match.away_team}`;
      return {
        id: `missing-recap-${match.id}`,
        lane: "drafting",
        title: label,
        meta: match.date || "Match",
        description:
          "No match recap has been saved yet. Add a recap if this match needs a team follow-up.",
        tone: "default",
        action: { kind: "summaries", matchId: match.id },
      };
    });

  return {
    summary: {
      needsTouch: needsTouchCards.length,
      followUp: followUpCards.length + staleGoalCards.length,
      unreviewed: unreviewedAssignedItems.length,
      missingRecaps: missingRecapCards.length,
    },
    lanes: [
      {
        id: "needs_touch",
        title: "Needs touch",
        description: "Players with no recent feedback or an obvious gap.",
        cards: needsTouchCards,
      },
      {
        id: "follow_up",
        title: "Follow-up",
        description: "Player responses and signals that ask for coach action.",
        cards: [...followUpCards, ...staleGoalCards].slice(
          0,
          MAX_CARDS_PER_LANE,
        ),
      },
      {
        id: "shared",
        title: "Shared",
        description: "Feedback already sent but not reviewed yet.",
        cards: sharedCards,
      },
      {
        id: "drafting",
        title: "Recaps needed",
        description: "Recent matches without a team recap yet.",
        cards: missingRecapCards,
      },
    ],
  };
}

function isActiveGoal(goal: PlayerGoal): boolean {
  return goal.status !== "achieved" && goal.status !== "archived";
}

function isStaleGoal(goal: PlayerGoal): boolean {
  if (!goal.target_date) return false;
  const target = Date.parse(goal.target_date);
  return Number.isFinite(target) && target < Date.now();
}

/** One follow-up card per (player, signal), built from the backend's
 * typed `player_responses` feed. Previously the lane double-surfaced
 * each signal as BOTH a per-player `signal_summary` aggregate card AND
 * a per-response detail card — inflating the lane count and (because
 * `signal_summary` counts recap responses while the detail loop is
 * note/clip-only) mismatching the two. Grouping the typed responses is
 * the single source of truth: the count and the latest reflection both
 * come from the same note/clip-scoped feed. */
function buildFollowUpCards(
  engagement: CoachEngagement | undefined,
  canonicalFeedback: boolean,
): CoachFeedbackBoardCard[] {
  // Group key → accumulated responses, in first-seen order. The backend
  // sorts `player_responses` need_help-first then by recency, so the
  // first response per group is the most recent of the higher-priority
  // band and groups naturally band need_help before tried_this.
  const groups = new Map<
    string,
    {
      playerId: string;
      signal: "need_help" | "tried_this";
      label: string;
      latestBody: string;
      count: number;
      firstItemKind: "note" | "clip";
      firstItemId: number;
      ackTargets: CoachFeedbackAckTarget[];
    }
  >();

  // Phase B: typed, attributed player responses from the backend
  // (column-first signal, clips included) — no client-side
  // `[SIGNAL:*]` decode. need_help + tried_this responses on notes/
  // clips land in the follow-up lane; got_it and recap responses do not.
  for (const response of engagement?.player_responses ?? []) {
    if (response.signal !== "need_help" && response.signal !== "tried_this") {
      continue;
    }
    if (response.item_kind !== "note" && response.item_kind !== "clip") {
      continue;
    }
    const key = `${response.player_id}:${response.signal}`;
    // A response with no user_id cannot be acked — it names no row. It
    // still counts toward the card, it just contributes no target.
    const target: CoachFeedbackAckTarget | null = response.user_id
      ? { user_id: response.user_id, item_kind: response.item_kind, item_id: response.item_id }
      : null;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      if (!existing.latestBody && response.body) {
        existing.latestBody = response.body;
      }
      if (target) existing.ackTargets.push(target);
      continue;
    }
    groups.set(key, {
      playerId: response.player_id,
      signal: response.signal,
      label: playerLabel({
        display_name: response.player_display_name,
        jersey_number: response.jersey_number,
      }),
      latestBody: response.body,
      count: 1,
      firstItemKind: response.item_kind,
      firstItemId: response.item_id,
      ackTargets: target ? [target] : [],
    });
  }

  return Array.from(groups.values()).map((group) => {
    const noun = group.signal === "need_help" ? "need-help" : "tried-this";
    const fallback =
      group.signal === "need_help"
        ? "A linked player or guardian asked for help on recent feedback."
        : "The player says they worked on feedback. Consider closing the loop.";
    return {
      id: `response-${group.signal}-${group.playerId}`,
      lane: "follow_up" as const,
      title: group.label,
      meta: `${group.count} ${noun} response${group.count === 1 ? "" : "s"}`,
      description: group.latestBody || fallback,
      tone: group.signal === "need_help" ? ("flag" as const) : ("good" as const),
      playerId: group.playerId,
      ackTargets: group.ackTargets,
      // A single response links straight to its item (the precise
      // action); multiple collapse to the player's roster row filtered
      // by signal (player-level triage). The canonical Library cannot
      // open the legacy item, so there every group takes the roster row.
      action:
        group.count === 1 && !canonicalFeedback
          ? group.firstItemKind === "clip"
            ? { kind: "clips" as const, itemId: group.firstItemId }
            : { kind: "notes" as const, itemId: group.firstItemId }
          : {
              kind: "roster" as const,
              playerId: group.playerId,
              context: group.signal,
            },
    };
  });
}

/** The Shared lane's description: WHO still has not opened the item.
 *
 * The lane admits an item only when it is assigned to someone and
 * reviewed by nobody (`services/engagement.py`: `if pids and not
 * scoped_item_reviews`), so a reach ratio here is always "Seen 0/N" and
 * vanishes the moment anyone opens it — a constant dressed as a signal.
 * The names are the actionable part: chasing Ava is a different
 * conversation from chasing Ben.
 *
 * Falls back to a count whenever ANY id fails to resolve — a vaguer true
 * sentence beats a confident wrong one, matching `sharedWithSentence`.
 * Partial resolution is the case that matters: `activePlayersById` holds
 * active players only, while the backend deliberately ships inactive
 * ones (`services/engagement.py` builds its player map with
 * `include_inactive=True`), so a player tagged on a note and later
 * deactivated stays in that item's `player_ids`. Naming only the
 * resolvable subset would assert that fewer people have it outstanding
 * than actually do — and this lane surfaces the oldest unopened items,
 * exactly the ones most likely to carry a departed player.
 */
function notOpenedSentence(
  playerIds: readonly string[] | undefined,
  playersById: ReadonlyMap<string, Player>,
): string {
  const ids = playerIds ?? [];
  // No tagged players: the item reached the lane via a team-visible
  // assignment, so there is nobody specific to name.
  if (ids.length === 0) {
    return "Shared with a player or family account, but not reviewed yet.";
  }

  const named = ids
    .map((id) => playersById.get(id))
    .filter((p): p is Player => !!p)
    .map((p) => playerLabel(p));

  // Any unresolved id means the names on hand are an incomplete answer
  // to "who still has this outstanding", so count rather than name.
  if (named.length < ids.length) {
    return `Not opened yet by ${ids.length} player${ids.length === 1 ? "" : "s"}.`;
  }
  if (named.length === 1) return `Not opened yet by ${named[0]}.`;
  if (named.length === 2) {
    return `Not opened yet by ${named[0]} and ${named[1]}.`;
  }
  // Two names plus a count: a board card is one line, and the third name
  // is what pushes it to wrap.
  return `Not opened yet by ${named[0]}, ${named[1]}, +${named.length - 2} more.`;
}

function playerLabel(player: {
  display_name: string;
  jersey_number?: string | null;
}): string {
  return player.jersey_number
    ? `#${player.jersey_number} ${player.display_name}`
    : player.display_name;
}

function fallbackItemTitle(item: EngagementListItem): string {
  return `${item.kind} ${item.item_id}`;
}

function sharedFallbackAction(
  item: EngagementListItem,
  canonicalFeedback: boolean,
): CoachFeedbackBoardAction {
  // The canonical Library cannot open a legacy item, so the card takes
  // the player's row — or the Players list for a team-wide item — which
  // is where the bell links the same item. The list gets no arrival
  // context: Players would select its first row and pin the context to
  // a player the item never named.
  if (canonicalFeedback) {
    const playerId = item.player_ids?.[0];
    return playerId
      ? { kind: "roster", playerId, context: "unreviewed_feedback" }
      : { kind: "roster" };
  }
  // Shared cards already keep `playerId` for the primary Give feedback
  // action. Their secondary action opens the shared evidence itself so the
  // coach keeps the note, clip or recap context shown by the approved Board.
  if (item.kind === "clip") return { kind: "clips", itemId: item.item_id };
  if (item.kind === "summary") {
    return {
      kind: "recaps",
      itemId: item.item_id,
      matchId: item.match_id ?? undefined,
    };
  }
  return { kind: "notes", itemId: item.item_id };
}
